import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { navLinks } from '@/mocks/home';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center">
              <i className={`ri-book-2-line text-2xl md:text-[26px] ${scrolled ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <div>
              <span className={`font-heading text-lg md:text-xl font-bold leading-none block ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
                {t('common:site_name')}
              </span>
              <span className={`text-[10px] md:text-xs leading-none block mt-0.5 ${scrolled ? 'text-foreground-500' : 'text-background-50/70'}`}>
                {t('common:site_tagline')}
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link, idx) => (
              <span key={link.path} className="flex items-center">
                {idx > 0 && (
                  <span className={`mx-1 text-xs ${scrolled ? 'text-foreground-300' : 'text-background-50/40'}`}>◆</span>
                )}
                <Link
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                    location.pathname === link.path
                      ? scrolled
                        ? 'text-primary-500 bg-primary-50'
                        : 'text-background-50 bg-background-50/15'
                      : scrolled
                        ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                        : 'text-background-50/85 hover:text-background-50 hover:bg-background-50/10'
                  }`}
                >
                  {link.name}
                </Link>
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/about"
              className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  : 'bg-background-50/20 text-background-50 hover:bg-background-50/30 backdrop-blur-sm'
              }`}
            >
              <i className="ri-user-3-line text-base w-4 h-4 flex items-center justify-center"></i>
              {t('common:read_now')}
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                scrolled ? 'text-foreground-950 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="mt-2 flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
            >
              <i className="ri-user-3-line text-base w-4 h-4 flex items-center justify-center"></i>
              {t('common:read_now')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}